<script setup lang="ts">
import { usePage } from '@inertiajs/vue3';
import { computed, useSlots } from 'vue';
import ToolHowToCard from '@/components/Tools/ToolHowToCard.vue';
import ToolImageOtherTools from '@/components/Tools/ToolImageOtherTools.vue';
import ToolPdfOtherTools from '@/components/Tools/ToolPdfOtherTools.vue';
import { useSite } from '@/composables/useSite';
import { useTranslations } from '@/composables/useTranslations';

type HowToPlacement = 'sidebar' | 'below_preview';

type PreviewTrack = 'default' | 'wide' | 'editor';

type Props = {
    title: string;
    subtitle?: string;
    showHowTo?: boolean;
    /**
     * Where the “How to use” card appears when a #preview slot is present.
     * `below_preview`: first column stacks preview then how-to (e.g. bulk tools).
     */
    howToPlacement?: HowToPlacement;
    /** Wider first column when `howToPlacement` is `below_preview` (image tools, including bulk). */
    previewTrack?: PreviewTrack;
    /** No max-width cap (e.g. image editor). */
    fluid?: boolean;
    /** Stack toolbar then full-width preview in one column (hides side-by-side upload column). */
    fullWidthPreview?: boolean;
    /** Show browser-only privacy copy instead of server upload retention text. */
    clientSideProcessing?: boolean;
    /** Translation key for client-side privacy notice (defaults to editor-specific copy). */
    clientSideNoticeKey?: string;
};

const props = withDefaults(defineProps<Props>(), {
    subtitle: '',
    showHowTo: true,
    howToPlacement: 'sidebar',
    previewTrack: 'default',
    fluid: false,
    fullWidthPreview: false,
    clientSideProcessing: false,
    clientSideNoticeKey: 'tools.data_retention_notice_client',
});

const page = usePage();
const { isImageSite, isPdfSite } = useSite();
const { t } = useTranslations();
const slots = useSlots();

const hasPreview = computed(() => typeof slots.preview === 'function');

const stackHowToUnderPreview = computed(
    () =>
        hasPreview.value &&
        props.showHowTo &&
        props.howToPlacement === 'below_preview',
);

const useFullWidthPreviewLayout = computed(
    () => props.fullWidthPreview && hasPreview.value,
);

const containerClass = computed(() =>
    props.fluid
        ? 'mx-auto w-full max-w-none px-3 py-6 sm:px-4 sm:py-8'
        : 'mx-auto w-full max-w-7xl px-3 py-6 sm:px-4 sm:py-8',
);

const gridClass = computed(() => {
    if (useFullWidthPreviewLayout.value) {
        return 'grid grid-cols-1 gap-3 sm:gap-4';
    }

    if (stackHowToUnderPreview.value) {
        if (props.previewTrack === 'editor') {
            return 'grid grid-cols-1 gap-3 sm:gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,17rem)] xl:grid-cols-[minmax(0,1fr)_minmax(0,19rem)]';
        }

        if (props.previewTrack === 'wide') {
            return 'xl:grid-cols-[minmax(0,42rem)_minmax(0,1fr)]';
        }

        return 'xl:grid-cols-[minmax(0,30rem)_minmax(0,1fr)]';
    }

    if (hasPreview.value && props.showHowTo) {
        return 'xl:grid-cols-[minmax(0,16rem)_minmax(0,1fr)_minmax(0,18rem)]';
    }

    if (hasPreview.value && !props.showHowTo) {
        return 'xl:grid-cols-[minmax(0,16rem)_minmax(0,1fr)]';
    }

    if (props.showHowTo) {
        return 'xl:grid-cols-[minmax(0,1fr)_minmax(0,18rem)]';
    }

    return '';
});

const previewAsideClass = computed(() => {
    if (useFullWidthPreviewLayout.value) {
        return props.showHowTo
            ? 'min-w-0 flex w-full flex-col gap-4'
            : 'min-w-0 w-full';
    }

    const base = 'min-w-0 xl:sticky xl:top-24 xl:self-start';

    if (stackHowToUnderPreview.value) {
        if (props.previewTrack === 'editor') {
            return `${base} flex min-h-0 flex-1 flex-col gap-4`;
        }

        return `${base} flex flex-col gap-5 xl:max-h-[calc(100vh-5.5rem)] xl:overflow-y-auto`;
    }

    return base;
});

const toolBundle = computed(() => {
    let path = (page.url || '').split('?')[0].replace(/^\/+/, '');
    path = path.replace(/^(en|sr)\//, '');

    if (path.startsWith('tools/pdf/')) {
        return {
            ns: 'pdf' as const,
            key: path.slice('tools/pdf/'.length).replace(/-/g, '_'),
        };
    }

    if (path.startsWith('tools/image/')) {
        return {
            ns: 'image' as const,
            key: path.slice('tools/image/'.length).replace(/-/g, '_'),
        };
    }

    return null;
});

const displayTitle = computed(() => {
    const bundle = toolBundle.value;

    if (!bundle) {
        return props.title;
    }

    if (bundle.ns === 'pdf') {
        return t(`tools.pdf.${bundle.key}.title`, props.title);
    }

    return t(`tools.image.${bundle.key}.title`, props.title);
});

const displaySubtitle = computed(() => {
    const bundle = toolBundle.value;

    if (!bundle) {
        return props.subtitle;
    }

    if (bundle.ns === 'pdf') {
        return t(`tools.pdf.${bundle.key}.description`, props.subtitle);
    }

    return t(`tools.image.${bundle.key}.description`, props.subtitle);
});

const showImageOtherTools = computed(
    () =>
        isImageSite.value &&
        toolBundle.value?.ns === 'image' &&
        Boolean(toolBundle.value.key),
);

const showPdfOtherTools = computed(
    () =>
        isPdfSite.value &&
        toolBundle.value?.ns === 'pdf' &&
        Boolean(toolBundle.value.key),
);

const otherToolsExcludeKey = computed(() => toolBundle.value?.key ?? '');

const sideSlotClass = computed(() => {
    if (
        stackHowToUnderPreview.value &&
        props.previewTrack === 'editor' &&
        hasPreview.value
    ) {
        return 'min-w-0 lg:max-w-[19rem] lg:justify-self-stretch';
    }

    return hasPreview.value ? 'min-w-0' : '';
});
</script>

<template>
    <div :class="containerClass">
        <header class="mb-4 sm:mb-6">
            <h1
                class="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl dark:text-gray-100"
            >
                {{ displayTitle }}
            </h1>
            <p
                v-if="displaySubtitle"
                class="mt-1 text-sm leading-relaxed text-pretty text-gray-500 dark:text-gray-400"
            >
                {{ displaySubtitle }}
            </p>
            <p
                class="mt-2 max-w-3xl text-xs leading-relaxed text-pretty text-muted-foreground"
            >
                {{
                    clientSideProcessing
                        ? t(
                              props.clientSideNoticeKey,
                              'Processing happens in your browser: your file is not uploaded to our servers.',
                          )
                        : t(
                              'tools.data_retention_notice',
                              'We do not keep your uploads after a tool run finishes: files are stored only temporarily to perform the operation, then deleted from the server (generated downloads are removed after they are sent, where applicable).',
                          )
                }}
            </p>
        </header>

        <div class="grid items-start gap-3 sm:gap-4" :class="gridClass">
            <template v-if="useFullWidthPreviewLayout">
                <div class="min-w-0">
                    <slot />
                </div>
                <aside :class="previewAsideClass">
                    <slot name="preview" />
                    <ToolHowToCard
                        v-if="stackHowToUnderPreview && props.showHowTo"
                        class="shrink-0"
                    />
                </aside>
            </template>

            <template v-else>
                <aside v-if="hasPreview" :class="previewAsideClass">
                    <div class="min-w-0 shrink-0">
                        <slot name="preview" />
                    </div>
                    <ToolHowToCard
                        v-if="stackHowToUnderPreview"
                        class="shrink-0"
                    />
                </aside>

                <div :class="sideSlotClass">
                    <slot />
                </div>

                <ToolHowToCard
                    v-if="props.showHowTo && !stackHowToUnderPreview"
                />
            </template>
        </div>

        <ToolImageOtherTools
            v-if="showImageOtherTools"
            :exclude-key="otherToolsExcludeKey"
        />
        <ToolPdfOtherTools
            v-if="showPdfOtherTools"
            :exclude-key="otherToolsExcludeKey"
        />
    </div>
</template>
